import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Event from "@material-ui/icons/Event";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import { deadlineDate, formatDeadline } from "./taskSorting";
import { googleCalendarUrl } from "./googleCalendar";
import { eventTimestamp, localEventTime, validEventTimes } from "./eventTimes";

export const userName = (user) => user.fullName || user.userName;

const useStyles = makeStyles((theme) => ({
  paper: { height: "85vh" },
  content: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { overflowY: "auto" },
  },
  panes: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down("sm")]: { display: "block", flex: "none" },
  },
  editor: {
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("sm")]: { overflowY: "visible" },
  },
  activity: {
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2, 3),
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      overflowY: "visible",
      borderLeft: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
}));

export default function TaskDialog({
  selection,
  lists,
  users,
  onClose,
  onChanged,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [action, setAction] = useState("");
  const [loaded, setLoaded] = useState(false);
  const mutation = useRef(false);
  const fillForm = (item) =>
    setForm({
      title: item.title,
      description: item.description,
      deadline: deadlineDate(item.deadline),
      eventStart: localEventTime(item.eventStart),
      eventEnd: localEventTime(item.eventEnd),
      backgroundColor: item.backgroundColor || null,
      color: item.color || null,
      TaskListId: item.TaskListId,
      Assignees: item.Assignees || [],
    });

  useEffect(() => {
    let active = true;
    if (!selection.id) {
      const item = {
        title: "",
        description: "",
        TaskListId: selection.TaskListId,
        Assignees: [],
      };
      setTask(item);
      fillForm(item);
      setLoaded(true);
    } else {
      taskRequest("get_task", { taskId: selection.id })
        .then(({ task: item }) => {
          if (active) {
            setTask(item);
            fillForm(item);
            setLoaded(true);
          }
        })
        .catch((err) => {
          if (active) setError(err.message);
        });
    }
    return () => {
      active = false;
    };
  }, [selection]);

  const run = async (operation) => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      await operation();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const save = () =>
    run(async () => {
      if (!validEventTimes(form)) return;
      await taskRequest("save_task", {
        taskId: task.id,
        revision: task.revision,
        task: {
          title: form.title,
          description: form.description,
          deadline: form.deadline || null,
          eventStart: eventTimestamp(form.eventStart),
          eventEnd: eventTimestamp(form.eventEnd),
          backgroundColor: form.backgroundColor,
          color: form.color,
          TaskListId: form.TaskListId,
          assigneeIds: form.Assignees.map((user) => user.id),
        },
      });
      onChanged();
      onClose();
    });
  const archive = () =>
    run(async () => {
      await taskRequest("save_task", {
        taskId: task.id,
        revision: task.revision,
        task: { archived: !task.archived },
      });
      onChanged();
      onClose();
    });
  const addEntry = (isComment) =>
    run(async () => {
      await taskRequest(isComment ? "add_task_comment" : "add_task_action", {
        taskId: task.id,
        text: isComment ? comment : action,
      });
      if (isComment) setComment("");
      else setAction("");
      // Keep the editor revision: a concurrent edit must still raise a conflict.
      const { task: item } = await taskRequest("get_task", { taskId: task.id });
      setTask((previous) => ({
        ...previous,
        Notes: item.Notes,
        TaskActions: item.TaskActions,
      }));
    });
  const labelValue = (key, value) => {
    if (key === "eventStart" || key === "eventEnd") return value ? new Date(value).toLocaleString() : t("tasks.noEventTime");
    if (key === "backgroundColor" || key === "color")
      return value || t("tasks.defaultColor");
    if (key === "deadline")
      return value ? formatDeadline(value) : t("tasks.noDeadline");
    if (key === "TaskListId")
      return (
        (lists.find((list) => list.id === value) || {}).name || `#${value}`
      );
    if (key === "assigneeIds")
      return (
        value
          .map((id) =>
            userName(
              users.find((user) => user.id === id) || { fullName: `#${id}` },
            ),
          )
          .join(", ") || t("tasks.unassigned")
      );
    if (key === "archived") return t(value ? "tasks.archived" : "tasks.active");
    return value || "—";
  };
  const options = users;
  const dirty =
    task &&
    form &&
    (form.title !== task.title ||
      form.description !== task.description ||
      form.deadline !== deadlineDate(task.deadline) ||
      form.eventStart !== localEventTime(task.eventStart) ||
      form.eventEnd !== localEventTime(task.eventEnd) ||
      form.TaskListId !== task.TaskListId ||
      form.backgroundColor !== (task.backgroundColor || null) ||
      form.color !== (task.color || null) ||
      JSON.stringify(form.Assignees.map((user) => user.id).sort()) !==
        JSON.stringify((task.Assignees || []).map((user) => user.id).sort()));

  const eventTimesValid = !form || validEventTimes(form);
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.paper }}
      aria-labelledby="task-dialog-title"
    >
      <DialogTitle id="task-dialog-title">
        {t(selection.id ? "tasks.taskDetails" : "tasks.addCard")}
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}
        {!loaded && !error && <CircularProgress />}
        {loaded && (
          <div className={classes.panes}>
            <section className={classes.editor} aria-label={t("tasks.taskDetails")}>
              {task.archived && (
                <Alert severity="info">{t("tasks.archivedNotice")}</Alert>
              )}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                {["backgroundColor", "color"].map((key) => (
                  <TextField
                    key={key}
                    type="color"
                    margin="normal"
                    id={`task-${key}`}
                    label={t(`tasks.fields.${key}`)}
                    InputLabelProps={{ shrink: true }}
                    style={{ flex: "1 1 160px" }}
                    value={
                      form[key] ||
                      (key === "backgroundColor" ? "#ffffff" : "#000000")
                    }
                    helperText={form[key] || t("tasks.defaultColor")}
                    disabled={busy || task.archived}
                    onChange={(event) =>
                      setForm({ ...form, [key]: event.target.value })
                    }
                  />
                ))}
                <Button
                  disabled={
                    busy ||
                    task.archived ||
                    (!form.backgroundColor && !form.color)
                  }
                  onClick={() =>
                    setForm({ ...form, backgroundColor: null, color: null })
                  }
                >
                  {t("tasks.resetColors")}
                </Button>
              </div>
              <Paper
                variant="outlined"
                style={{
                  padding: 16,
                  margin: "8px 0 16px",
                  backgroundColor: form.backgroundColor || undefined,
                  color: form.color || undefined,
                }}
              >
                <Typography variant="caption" color="inherit">
                  {t("tasks.colorPreview")}
                </Typography>
                <Typography
                  color="inherit"
                  style={{ fontWeight: 600, overflowWrap: "anywhere" }}
                >
                  {form.title || t("tasks.addCard")}
                </Typography>
              </Paper>
              <TextField
                autoFocus={!task.id}
                fullWidth
                margin="normal"
                id="task-title"
                label={t("tasks.title")}
                value={form.title}
                disabled={busy || task.archived}
                inputProps={{ maxLength: 255 }}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <TextField
                fullWidth
                margin="normal"
                multiline
                rows={4}
                variant="outlined"
                id="task-description"
                label={t("tasks.description")}
                value={form.description}
                disabled={busy || task.archived}
                inputProps={{ maxLength: 20000 }}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
              <TextField
                fullWidth
                margin="normal"
                type="date"
                id="task-deadline"
                label={t("tasks.deadline")}
                value={form.deadline}
                disabled={busy || task.archived}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "1000-01-01", max: "9999-12-31" }}
                helperText={t("tasks.deadlineOptional")}
                onChange={(event) =>
                  setForm({ ...form, deadline: event.target.value })
                }
              />

              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                {["eventStart", "eventEnd"].map((key) => (
                  <TextField key={key} type="datetime-local" margin="normal" id={`task-${key}`}
                    label={t(`tasks.fields.${key}`)} InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 1, min: "1000-01-01T00:00", max: "9999-12-31T23:59:59" }}
                    style={{ flex: "1 1 240px" }} value={form[key]} disabled={busy || task.archived}
                    error={!eventTimesValid} onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
                ))}
              </div>
              <Typography variant="caption" color={eventTimesValid ? "textSecondary" : "error"} display="block">
                {eventTimesValid ? t("tasks.eventTimesHelp", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : t("tasks.invalidEventTimes")}
              </Typography>
              <Button size="small" disabled={busy || task.archived || (!form.eventStart && !form.eventEnd)}
                onClick={() => setForm({ ...form, eventStart: "", eventEnd: "" })}>{t("tasks.clearEventTimes")}</Button>
              {task.id && !task.archived && (
                <div style={{ margin: "8px 0 16px" }}>
                  <Button
                    component="a"
                    href={form.title.trim() && eventTimesValid ? googleCalendarUrl(form) : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    startIcon={<Event />}
                    disabled={busy || !form.title.trim() || !eventTimesValid}
                    aria-describedby="task-calendar-help"
                  >
                    {t("tasks.createCalendarEvent")}
                  </Button>
                  <Typography id="task-calendar-help" variant="caption" color="textSecondary" display="block">
                    {t("tasks.calendarEventHelp")}
                  </Typography>
                </div>
              )}
              <TextField
                fullWidth
                select
                margin="normal"
                id="task-list"
                label={t("tasks.list")}
                value={form.TaskListId}
                disabled={busy || task.archived}
                onChange={(e) => setForm({ ...form, TaskListId: e.target.value })}
              >
                {lists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                id="task-assignees"
                multiple
                options={options}
                getOptionLabel={userName}
                getOptionSelected={(a, b) => a.id === b.id}
                value={form.Assignees}
                disabled={busy || task.archived}
                onChange={(event, value) =>
                  setForm({ ...form, Assignees: value })
                }
                renderTags={(value, getTagProps) =>
                  value.map((user, index) => (
                    <Chip label={userName(user)} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    variant="outlined"
                    label={t("tasks.assignees")}
                  />
                )}
              />
              {task.id && (
                <>
                  <Divider style={{ margin: "20px 0" }} />
                  <Typography variant="h6">{t("tasks.comments")}</Typography>
                  {!task.archived && (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        variant="outlined"
                        id="task-comment"
                        label={t("tasks.writeComment")}
                        value={comment}
                        disabled={busy}
                        inputProps={{ maxLength: 20000 }}
                        onChange={(e) => setComment(e.target.value)}
                      />
                      <Button
                        disabled={busy || !comment.trim()}
                        onClick={() => addEntry(true)}
                        color="primary"
                      >
                        {t("tasks.addComment")}
                      </Button>
                    </>
                  )}
                  {!(task.Notes || []).length && (
                    <Typography color="textSecondary">
                      {t("tasks.noComments")}
                    </Typography>
                  )}
                  {(task.Notes || []).map((item) => (
                    <div key={item.id} style={{ margin: "16px 0" }}>
                      <Typography variant="caption" color="textSecondary">
                        {item.User ? userName(item.User) : t("tasks.unknownUser")}{" "}
                        · {new Date(item.createdAt).toLocaleString()}
                      </Typography>
                      <Typography
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </>
              )}
            </section>
            <aside className={classes.activity} aria-labelledby="task-activity-title">
              <Typography id="task-activity-title" component="h2" variant="h6">{t("tasks.activity")}</Typography>
              {!task.id ? (
                <Typography color="textSecondary">{t("tasks.saveForActivity")}</Typography>
              ) : (
                <>
                  {!task.archived && (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        variant="outlined"
                        id="task-action"
                        label={t("tasks.actionTaken")}
                        value={action}
                        disabled={busy}
                        inputProps={{ maxLength: 20000 }}
                        onChange={(e) => setAction(e.target.value)}
                      />
                      <Button
                        disabled={busy || !action.trim()}
                        onClick={() => addEntry(false)}
                        color="primary"
                      >
                        {t("tasks.addAction")}
                      </Button>
                    </>
                  )}
                  {(task.TaskActions || []).map((item) => (
                    <div
                      key={item.id}
                      style={{ margin: "16px 0", overflowWrap: "anywhere" }}
                    >
                      <Typography variant="body2">
                        {item.User ? userName(item.User) : t("tasks.unknownUser")}{" "}
                        · {t(`tasks.actionTypes.${item.type}`)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(item.createdAt).toLocaleString()}
                      </Typography>
                      {item.text && (
                        <Typography style={{ whiteSpace: "pre-wrap" }}>
                          {item.text}
                        </Typography>
                      )}
                      {Object.entries(item.changes || {})
                        .filter(
                          ([key]) => key !== "commentId" && key !== "noteId",
                        )
                        .map(([key, change]) => (
                          <Typography
                            key={key}
                            variant="body2"
                            color="textSecondary"
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {t(`tasks.fields.${key}`)}:{" "}
                            {labelValue(key, change.from)} →{" "}
                            {labelValue(key, change.to)}
                          </Typography>
                        ))}
                    </div>
                  ))}
                  {!(task.TaskActions || []).length && (
                    <Typography color="textSecondary">{t("tasks.noActivity")}</Typography>
                  )}
                </>
              )}
            </aside>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ flexWrap: "wrap" }}>
        {busy && <CircularProgress size={20} />}
        {task && task.id && (
          <Button disabled={busy || dirty} onClick={archive}>
            {t(task.archived ? "tasks.restore" : "tasks.archive")}
          </Button>
        )}
        {dirty && task.id && (
          <Typography variant="caption">
            {t("tasks.saveBeforeArchive")}
          </Typography>
        )}
        <Button disabled={busy} onClick={onClose}>
          {t("tasks.close")}
        </Button>
        {loaded && !task.archived && (
          <Button
            disabled={busy || !form.title.trim() || !form.TaskListId || !eventTimesValid}
            onClick={save}
            color="primary"
            variant="contained"
          >
            {t("tasks.save")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
